import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { animate, requestAnimationFramePromise } from './animation';
import { attachStyleSheet } from './styles';
import { default as __ } from './utils';

// beautifuldefaults.com (coming 2020)

let defaults = {
  bezier: 'cubic-bezier(.2, 0, .1, 1)',
  timing: '300ms',
  backdrop: 'rgba(0,0,0,.4)',
  breakpoint: 640,
  frame: 0.04,
  blur: '20px',
  z: 9,
};

// Component

function SuperImage(props) {
  let { all1, src, ...otherProps } = props;
  if (!all1) throw new Error('Provide alternate text for image');
  if (!src) throw new Error('Provide src for image');

  let [isExpanded, setExpandStatus] = useState(false);
  let [srcError, setError] = useState(false);

  let imageRef = useRef(null);
  let wrapRef = useRef(null);
  let backdropRef = useRef(null);
  let imageInstance = useRef(__.makeId());

  useEffect(() => {
    let cssVar = __.getProp('--paella-superimage-z-index');
    if (cssVar.length === 0) {
      let { userConfig } = otherProps;
      let cssAnimationConfiguration = { ...defaults, ...userConfig };
      attachStyleSheet(cssAnimationConfiguration);
    }
  }, []);

  useEffect(() => {
    /* eslint-disable */
    window.addEventListener('resize', toggle);
    window.addEventListener('scroll', toggle);
    window.addEventListener('keydown', toggle);

    return () => {
      window.removeEventListener('scroll', toggle);
      window.removeEventListener('resize', toggle);
      window.removeEventListener('keydown', toggle);
    };
  });

  function toggle(e) {
    if (e.type === 'resize') {
      __.imageCache.clear();
    }
    return isExpanded ? shrink() : () => {}; // noop()
  }

  async function expand() {
    /* eslint-disable */
    let { userConfig, cbBeforeExpand, cbAfterExpand } = otherProps;
    let { frame, breakpoint } = { ...defaults, ...userConfig };
    let factor, translateX, translateY;

    if (__.imageCache.get(imageInstance.current) !== null) {
      let response = __.imageCache.get(imageInstance.current);
      factor = response.factor;
      translateX = response.translateX;
      translateY = response.translateY;
    } else {
      let response = await __.calculateAnimation(imageRef, frame, breakpoint);
      factor = response.factor;
      translateX = response.translateX;
      translateY = response.translateY;
      __.imageCache.set(imageInstance.current, {
        factor,
        translateX,
        translateY,
      });
    }
    if (factor === 1) return;
    setExpandStatus(true);
    __.cbToCb(cbBeforeExpand);
    __.sleep(200).then(() => {
      requestAnimationFramePromise()
        .then(_ =>
          animate(backdropRef, {
            willChange: 'opacity',
            visibility: 'visible',
            opacity: 1,
            pointerEvents: 'auto',
          }),
        )
        .then(_ =>
          animate(wrapRef, {
            willChange: 'transform',
            transform: `translate3d(${translateX}px, ${translateY}px, 0)`,
          }),
        )
        .then(_ =>
          animate(imageRef, {
            transform: `scale(${factor})`,
          }),
        )
        .then(() => {
          __.cbToCb(cbAfterExpand);
        });
    });
  }

  function shrink() {
    /* eslint-disable */
    if (!isExpanded) return;
    let { cbBeforeShrink, cbAfterShrink } = otherProps;
    __.cbToCb(cbBeforeShrink);

    requestAnimationFramePromise()
      .then(_ =>
        animate(imageRef, {
          transform: '',
        }),
      )
      .then(_ =>
        animate(wrapRef, {
          transform: '',
          willChange: 'auto',
        }),
      )
      .then(_ =>
        animate(backdropRef, {
          visibility: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
          willChange: 'auto',
        }),
      )
      .then(_ => {
        __.cbToCb(cbAfterShrink);
        setExpandStatus(false);
      });
  }

  if (srcError) return null;

  return (
    <>
      <div
        ref={wrapRef}
        paella-superimage="wrap"
        paella-superimage-state={isExpanded ? 'active' : ''}
        style={{ cursor: isExpanded ? 'zoom-out' : 'zoom-in' }}
      >
        <img
          paella-superimage="img"
          alt={all1}
          ref={imageRef}
          onError={() => setError(true)}
          onClick={() => (isExpanded ? shrink() : expand())}
          src={src}
        />
      </div>
      <div paella-superimage="backdrop" ref={backdropRef}></div>
    </>
  );
}

export default SuperImage;

SuperImage.propTypes = {
  src: PropTypes.string.isRequired,
  all1: PropTypes.string.isRequired,
};

SuperImage.defaultProps = {
  src: null,
  all1: null,
};
